import { useMemo } from 'react';

import { useGetApiV1ClassroomdisciplineSharedClassStudents } from '@/shared/api/generated';

import type { TutorFilterOption } from '../types/tutorAi';
import { mapLookupOptions } from '../utils/mapLookupOptions';
import { LOOKUP_TAKE } from '../utils/tutorAiLimits';
import { useLookupSearchFilter } from './useLookupSearchFilter';

/**
 * Busca de turmas para o filtro do dashboard, com debounce e mínimo de caracteres.
 *
 * Consome `GET /api/v1/classroomdiscipline/shared/class-students` via hook gerado pelo
 * Kubb. O lookup aceita recorte por `CourseId`, mas a tela não o usa: o backend cria uma
 * turma por curso, então a turma já é o recorte mais fino que existe.
 *
 * @param filter - Texto digitado no campo de busca
 * @returns Opções encontradas e se a última query concluiu com sucesso
 *
 * @example
 * ```tsx
 * const { items, isQuerySuccess } = useTutorClassSearch(classSearch);
 * ```
 */
export function useTutorClassSearch(filter: string) {
  const { debouncedFilter, isReady, isPending } = useLookupSearchFilter(filter);

  const { data, isSuccess } = useGetApiV1ClassroomdisciplineSharedClassStudents(
    isReady ? { Search: debouncedFilter, Take: LOOKUP_TAKE } : undefined,
    { query: { enabled: isReady } },
  );

  const items: readonly TutorFilterOption[] = useMemo(
    () => (isPending ? [] : mapLookupOptions(data)),
    [isPending, data],
  );

  return { items, isQuerySuccess: !isPending && isSuccess } as const;
}
